import { useCurrentUser } from '@/composables/currentUser';
import { computed, watch, type ComputedRef } from 'vue';
import { createEventHook, createSharedComposable } from '@vueuse/core';
import {
    type Locale,
    setI18nLanguage,
    i18n,
    isLocaleMessagesLoaded,
} from '@/locales/config';
import { LOCALISATION_SELECTED } from '@/consts/storage';
import { useUpdateTeamMember } from './teamMembers';
import { setDefaultOptions } from 'date-fns';

interface UseLocaleImpl {
    /** Reactive computed reference to the current locale */
    locale: ComputedRef<Locale>;
    /** Function to set a new locale */
    setLocale: (locale: Locale) => void;
    /** Function to translate a key, provides a shortcut to `i18n.global.t` */
    // The function is returned in the `useLocale` composable to provide everything that is locales-related right in the composable.
    // you can keep using the normal `t` function from `useI18n`. both will work.
    t: typeof i18n.global.t;
    /** Function to check if a translation exists */
    translationExists: typeof i18n.global.te;
    /** Reactive computed reference to check if the current locale is right-to-left */
    isRTL: ComputedRef<boolean>;
    /** Reactive computed reference to the direction of the current locale */
    dir: ComputedRef<'ltr' | 'rtl'>;
    /** Hook to listen for locale changes */
    onLocaleChange: (callback: (locale: Locale) => void) => void;
    /** Reactive computed reference to check if locale messages are loaded */
    isLocaleReady: ComputedRef<boolean>;
}
/**
 * everything that is locales-related
 */
export const useLocale = createSharedComposable((): UseLocaleImpl => {
    const { user, onCurrentUserLoaded } = useCurrentUser();

    const event = createEventHook<Locale>();
    const locale = computed<Locale>(
        () => (i18n.global.locale.value as Locale) ?? 'en',
    );
    const isRTL = computed(() => locale.value === 'ar');
    const dir = computed(() => (isRTL.value ? 'rtl' : 'ltr'));

    let arLocale: typeof import('date-fns/locale/ar').ar;

    function setStoredLocale(loc: Locale) {
        localStorage.setItem(LOCALISATION_SELECTED, loc);
    }

    function setLocale(loc: Locale) {
        setI18nLanguage(loc).then(() => {
            setStoredLocale(loc);
            event.trigger(loc);
        });

        // Update user locale on BE
        if (loc === user.value?.locale) return;
        if (!user.value) return;

        const { update } = useUpdateTeamMember(user.value.id, {
            showToaster: false,
        });
        update({ name: user.value.name, locale: loc });
    }

    onCurrentUserLoaded((user) => {
        if (!user) return;

        setLocale(user.data.locale);
    });

    watch(
        locale,
        async (newLocale) => {
            if (newLocale === 'ar' && !arLocale) {
                arLocale = (await import('date-fns/locale/ar')).ar;
            }

            setDefaultOptions({
                locale: newLocale === 'ar' ? arLocale : undefined,
            });
        },
        { immediate: true },
    );

    const isLocaleReady = computed(() => isLocaleMessagesLoaded(locale.value));

    return {
        locale,
        setLocale,
        t: i18n.global.t,
        translationExists: i18n.global.te,
        isRTL,
        dir,
        onLocaleChange: event.on,
        isLocaleReady,
    };
});
